import type {
	ExpansionWatchStatus,
	WatchedExpansionDto,
} from "@repo/game-admin"
import { addDays, format, parseISO } from "date-fns"
import {
	type CompletionState,
	sealedCompletion,
	singlesCompletion,
} from "./completion"
import { awaitsApproval } from "./watch-changes"

export const LIST_PRESETS = [
	"open",
	"late",
	"due7",
	"review",
	"approve",
	"upcoming",
	"all",
] as const
/** Quick filters of the upcoming-expansions list. */
export type ListPreset = (typeof LIST_PRESETS)[number]

const CLOSED_OR_DONE = new Set(["ready", "ignored", "rejected"])

/** Whether a watched expansion belongs to a quick filter on a given day (`YYYY-MM-DD`). */
export function matchesPreset(
	row: WatchedExpansionDto,
	preset: ListPreset,
	today: string,
): boolean {
	const in7Days = format(addDays(parseISO(today), 7), "yyyy-MM-dd")
	const in60Days = format(addDays(parseISO(today), 60), "yyyy-MM-dd")

	switch (preset) {
		case "open":
			return !CLOSED_OR_DONE.has(row.status)
		case "late":
			return row.status === "late"
		case "due7":
			return (
				row.reviewState === "confirmed" &&
				row.status !== "late" &&
				row.readiness !== "ready" &&
				row.datePrecision === "day" &&
				row.releaseDate !== null &&
				row.releaseDate > today &&
				row.releaseDate <= in7Days
			)
		case "review":
			return row.status === "to_review"
		case "approve":
			return awaitsApproval(row)
		case "upcoming":
			return (
				row.reviewState === "confirmed" &&
				row.releaseDate !== null &&
				row.releaseDate >= today &&
				row.releaseDate <= in60Days
			)
		case "all":
			return true
		default: {
			const _exhaustive: never = preset
			return _exhaustive
		}
	}
}

/** The page's views; list and calendar share every filter. */
export type UpcomingView = "list" | "calendar" | "sources"

export const TODO_FILTERS = ["singles", "sealed"] as const
/** Keeps expansions whose singles or sealed products are not complete against TCGplayer. */
export type TodoFilter = (typeof TODO_FILTERS)[number]

/** Every filter of the list and the calendar; both views share them. */
export interface ExpansionFilters {
	preset: ListPreset
	/** Any of these statuses; empty keeps every expansion. */
	statuses: ExpansionWatchStatus[]
	/** Keeps only expansions whose alerts are muted. */
	muted: boolean
	/** Matched against the name and the code, ignoring case. */
	query: string
	/** `YYYY-MM-DD`, inclusive. A set bound hides undated expansions. */
	from: string | undefined
	/** `YYYY-MM-DD`, inclusive. A set bound hides undated expansions. */
	to: string | undefined
	/** Any of these still to do; empty keeps every expansion. */
	todo: TodoFilter[]
	/** Keeps only expansions the watcher changed in the last 7 days. */
	changed: boolean
}

const TO_DO: ReadonlySet<CompletionState> = new Set(["partial", "missing"])

function hasWorkLeft(row: WatchedExpansionDto, todo: TodoFilter[]): boolean {
	if (todo.length === 0) return true

	return todo.some((aspect) => {
		const completion =
			aspect === "singles" ? singlesCompletion(row) : sealedCompletion(row)
		return TO_DO.has(completion.state)
	})
}

/** Whether a watched expansion passes every filter on a given day (`YYYY-MM-DD`). */
export function matchesFilters(
	row: WatchedExpansionDto,
	filters: ExpansionFilters,
	today: string,
): boolean {
	if (!matchesPreset(row, filters.preset, today)) return false
	if (filters.statuses.length > 0 && !filters.statuses.includes(row.status)) {
		return false
	}
	if (filters.muted && row.mute === null) return false

	const query = filters.query.trim().toLowerCase()
	if (query && !`${row.name} ${row.code ?? ""}`.toLowerCase().includes(query)) {
		return false
	}

	if (filters.from || filters.to) {
		if (!row.releaseDate) return false
		if (filters.from && row.releaseDate < filters.from) return false
		if (filters.to && row.releaseDate > filters.to) return false
	}

	if (filters.changed && row.recentChanges.length === 0) return false

	return hasWorkLeft(row, filters.todo)
}

/** Search params of the page that its views and filters change; undefined clears one. */
export interface UpcomingSearchChange {
	view?: UpcomingView
	preset?: ListPreset
	statuses?: ExpansionWatchStatus[]
	muted?: boolean
	games?: string[]
	from?: string
	to?: string
	todo?: TodoFilter[]
	changed?: boolean
	month?: string
	entry?: string
}
